import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import useRequest from "hooks/useRequest";

import { EventRepository } from "helpers/EventRepository";

import i18n from "i18n";
import { PublicService } from "services/PublicService.jsx";

import { steps } from "./data/steps";
import Loader from "components/MainApp/atoms/Loader";
import AdminLayout from "components/MainApp/layouts/DesktopLayout";
import StepProgress from "components/MainApp/atoms/Wizard/StepProgress";
import StepOne from "./Wizard/Step1";
import StepTwo from "./Wizard/Step2";
import StepThree from "./Wizard/Step3";

import styles from "./AddProvider.module.scss";
import { ProviderService } from "../../../../services/ProviderService";
import { Provider } from "models/Persons";

const initialProvider = {
  type: "PROVIDER",
  name: "",
  business_name: "",
  nif: "",
  country: "",
  region: "",
  state: "",
  locality: "",
  entry_date: null,
  address: "",
  phone: "",
  email: "",
};

const AddProvider = (props) => {
  const history = useHistory();
  const {
    loading,
    beforeSubmit,
    afterSubmit,
    errors,
    dealWithError,
  } = useRequest();
  const [activeStep, setActiveStep] = useState("0");
  const [provider, setProvider] = useState(new Provider(initialProvider));
  const [countries, setcountries] = useState([]);
  const [regions, setregions] = useState([]);
  const [provinces, setprovinces] = useState([]);
  const [cities, setcities] = useState([]);
  const headerOptions = [];

  useEffect(() => {
    let countriesAux = [];
    PublicService.countries().then((response) => {
      countriesAux = response;
      setcountries(response);
    });

    if (props.match.params.key) {
      const id = props.match.params.key;

      beforeSubmit();
      ProviderService.provider(id).then((provider) => {
        let regions;
        let states;
        let localities;

        let regionsPromises = PublicService.regions(provider.country);
        let statesPromises = PublicService.states(
          provider.country,
          provider.region
        );
        let citiesPromises = PublicService.cities(
          provider.country,
          provider.region,
          provider.state
        );

        Promise.all([regionsPromises, statesPromises, citiesPromises]).then(
          (responses) => {
            regions = responses[0];
            setregions(regions);

            states = responses[1];
            setprovinces(states);

            localities = responses[2];
            setcities(localities);

            provider.country = countriesAux.find(
              (c) => c.value === provider.country
            );
            provider.locality = localities.find(
              (c) => c.value === provider.locality
            );
            provider.region = regions.find((c) => c.value === provider.region);
            provider.state = states.find((c) => c.value === provider.state);

            setProvider(provider);
            afterSubmit();
          }
        );
      });
    }
  }, []);

  const analizeErrors = (message, errors = {}) => {
    if (Object.keys(errors).length === 0) {
      EventRepository.notificationSend({
        label: message,
        type: "error",
      });
    }

    if (
      [
        "name",
        "business_name",
        "document",
        "state",
        "locality",
        "region",
        "entry_date",
      ].includes(Object.keys(errors)[0])
    ) {
      setActiveStep("0");
    }

    if (["email", "phone", "address"].includes(Object.keys(errors)[0])) {
      setActiveStep("1");
    }
  };

  const handleFinish = (e) => {
    e.preventDefault();
    let providerAux = { ...provider };
    providerAux.country = providerAux.country.value;
    providerAux.region = providerAux.region.value;
    providerAux.state = providerAux.state.value;
    providerAux.locality = providerAux.locality.value;

    providerAux.entry_date += " 12:00:00";

    beforeSubmit();
    if (providerAux.code) {
      ProviderService.updateProvider(providerAux.code, providerAux)
        .then((provider) => {
          afterSubmit();
          EventRepository.notificationSend({
            label: i18n.t("provider.updated.successfully"),
            type: "success",
          });
          history.push(`/admin/providers`);
        })
        .catch((error) => {
          dealWithError(error, "generic.error", (message, errors) => {
            analizeErrors(message, errors);
          });
          afterSubmit();
        });
    } else {
      ProviderService.createProvider(providerAux)
        .then((provider) => {
          setProvider(provider);
          afterSubmit();
          EventRepository.notificationSend({
            label: i18n.t("provider.added.successfully"),
            type: "success",
          });
          history.push(`/admin/providers`);
        })
        .catch((error) => {
          dealWithError(error, "generic.error", (message, errors) => {
            analizeErrors(message, errors);
          });
          afterSubmit();
        });
    }
  };

  const handleGoBack = () => {
    history.push(`/admin/providers`);
  };

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    let providerAux = { ...provider };
    providerAux[name] = value;

    setProvider(new Provider(providerAux));
  };

  const handleSelect = (selectedOption, type) => {
    let providerAux = { ...provider };
    switch (type) {
      case "country":
        providerAux.country = selectedOption;
        providerAux.region = "";
        providerAux.state = "";
        providerAux.locality = "";
        PublicService.regions(providerAux.country.value).then((response) => {
          setregions(response);
        });
        setProvider(new Provider(providerAux));

        break;
      case "region":
        providerAux.region = selectedOption;
        providerAux.state = "";
        providerAux.locality = "";
        PublicService.states(
          providerAux.country.value,
          providerAux.region.value
        ).then((response) => {
          setprovinces(response);
        });
        setProvider(new Provider(providerAux));

        break;
      case "state":
        providerAux.state = selectedOption;
        providerAux.locality = "";
        PublicService.cities(
          providerAux.country.value,
          providerAux.region.value,
          providerAux.state.value
        ).then((response) => {
          setcities(response);
        });
        setProvider(new Provider(providerAux));

        break;
      case "city":
        providerAux.locality = selectedOption;
        setProvider(new Provider(providerAux));

        break;

      default:
        break;
    }
  };

  const validateStep0 = () => {
    if (provider) {
      if(provider.country?.value === 'ES'){
        return (
          provider.name && provider.name.trim() !== "" &&
          provider.business_name && provider.business_name.trim() !== "" &&
          provider.document && provider.document.trim() !== "" &&
          provider.country && provider.country.value !== "" &&
          provider.region && provider.region.value !== "" &&
          provider.state && provider.state.value !== "" &&
          provider.locality && provider.locality.value !== "" &&
          provider.entry_date && provider.entry_date !== ""
        );
      }
      return (
        provider.name && provider.name.trim() !== "" &&
        provider.business_name && provider.business_name.trim() !== "" &&
        provider.document && provider.document.trim() !== "" &&
        provider.country && provider.country.value !== "" &&
        provider.entry_date && provider.entry_date !== ""
      );
    } else {
      return false;
    }
  };

  const validateStep1 = () => {
    if (provider) {
      return (
        provider.phone && provider.phone.trim() !== ""
      );
    } else {
      return false;
    }
  };

  const goToStep = (step) => {
    setActiveStep(step);
  };
  const handleContinue = (e, currentStep, validateStep) => {
    e.preventDefault();
    if (validateStep()) {
      setActiveStep(currentStep);
    }
  };

  const step0 = (
    <StepOne
      handleContinue={(e) => handleContinue(e, "1", validateStep0)}
      provider={provider}
      handleSelect={handleSelect}
      handleChange={handleChange}
      countries={countries}
      regions={regions}
      provinces={provinces}
      cities={cities}
      enableContinue={validateStep0()}
      error={errors}
    ></StepOne>
  );

  const step1 = (
    <StepTwo
      handleContinue={(e) => handleContinue(e, "2", validateStep1)}
      goBack={() => goToStep("0")}
      handleChange={handleChange}
      enableContinue={validateStep1()}
      provider={provider}
      error={errors}
    ></StepTwo>
  );

  const step2 = (
    <StepThree
      handleFinish={handleFinish}
      goBack={() => goToStep("1")}
      description={provider && provider.getDescriptionPanelView()}
      contact={provider && provider.getContactPanelView()}
    ></StepThree>
  );

  // const stepList = [step0, step1, step2];
  const stepList = {
    "0": step0,
    "1": step1,
    "2": step2,
  };

  const content = (
    <div className={styles[`add-provider-content`]}>
      <div className={styles[`add-provider-wrapper`]}>
        <StepProgress steps={steps} activeStep={activeStep} />
        <div className={styles[`add-provider-step-wrapper`]}>
          {stepList[activeStep]}
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles["add-provider-container"]}>
      <AdminLayout
        headerTitle={i18n.t("new.provider")}
        headerOptions={headerOptions}
        content={content}
        navHidden={true}
        goBackFunc={handleGoBack}
      ></AdminLayout>
      {loading && <Loader />}
    </div>
  );
};

export default AddProvider;
